import { createContext, useContext } from 'react';
import { view } from 'components/Search/Results/constants';

/**
 * SCIL-17603/17604: org-profile context for the SciSearch AI modal.
 *
 * The modal (NLPSearchModal) and its stage-two confirm (useConfirmSearch) are
 * shared verbatim between main search and the organisation profile. Rather than
 * fork them, the org profile provides this context; when it is non-null the
 * modal:
 *   - generates filters against the org-scoped endpoint and lists org recents,
 *   - applies accepted filters to the shared org People/Activity slice (via
 *     `applyOrgFilters`) instead of the main `search` slice,
 *   - switches the profile tab (People/Activity) to match the AI's classification
 *     via `runSearchOn`, the way main search switches view,
 *   - never renders follow-up UI.
 *
 * Main search renders no provider, so `useNlpOrgContext()` returns null and every
 * existing code path is untouched.
 *
 * @typedef {Object} NlpOrgContextValue
 * @property {string} organisationId
 * @property {string} organisationName
 * @property {1|3} entityType API EntityType of the active profile tab
 *   (People -> 1 Researcher, Activity -> 3): the view the modal starts from. The
 *   modal must derive its view from this instead of the main-search view, which
 *   can be 'organisation' (2) — a value the org-scoped endpoint rejects.
 * @property {string} filtersUrl org-scoped filter-generation endpoint
 * @property {string} recentSearchesUrl org-scoped recent-searches endpoint
 * @property {(enrichedFilters: Record<string, any>) => void} applyOrgFilters
 *   Applies accepted filters to the shared slice: full-reset -> merge mapped
 *   filters + session id. Does NOT run a search — the caller runs it via
 *   `runSearchOn` AFTER activating the session, so the baseline middleware
 *   sees the new session, not a stale one (session-activate then search, mirroring
 *   main search).
 * @property {(searchView: string) => void} runSearchOn
 *   Switches to the tab matching the canonical view (researcher -> People,
 *   activity -> Activity) and runs that tab's search. The stale mechanism
 *   refreshes the other tab on activation.
 * @property {(apiError: { errorCode?: string, rejectionReason?: string }) => boolean} isMainSearchRedirectRejection
 *   True when the org endpoint says the query belongs on main search; other
 *   rejections render the normal error box.
 * @property {(query: string) => void} onMainSearchRedirect
 *   Opens main search in a new tab with the query prefilled and closes the modal.
 */

/** @type {import('react').Context<NlpOrgContextValue | null>} */
export const NlpOrgContext = createContext(null);

/** @returns {NlpOrgContextValue | null} */
export const useNlpOrgContext = () => useContext(NlpOrgContext);

// Which modal a kept query belongs to (SCIL-18504): one org profile, or main search.
// Org People and Activity tabs share a key — it is the same modal on the same org.
export const MAIN_SEARCH_SURFACE = 'main-search';
export const nlpSurfaceKey = orgCtx =>
  orgCtx ? `org:${orgCtx.organisationId}` : MAIN_SEARCH_SURFACE;

const orgViewByEntityType = { 1: view.researcher, 3: view.activity };

// Unknown values return null so callers fall back to their neutral wording
// rather than silently claiming "Contact".
export const orgViewForEntityType = entityType =>
  orgViewByEntityType[entityType] ?? null;
